import { useEffect, useState } from 'react';
import { useMutation, graphql } from 'react-relay';
import { useSelector, useDispatch } from 'react-redux';
import {
    saveKlarnaSession,
    saveKlarnaClientToken,
    klarnaSessionUpdated,
} from '../actions/checkoutKlarnaActions';
import { initKlarnaSdk, loadKlarnaPaymentsWidget } from 'dibs-klarna/exports/klarnaSdk';

import { klarnaCreateSessionMutation } from './__generated__/klarnaCreateSessionMutation.graphql';
import { klarnaUpdateSessionMutation } from './__generated__/klarnaUpdateSessionMutation.graphql';

type Currency = 'USD' | 'GBP' | 'CHF' | 'EUR' | 'CAD' | 'AUD' | 'MXN';
export type TransactionType =
    | 'OFF_PLATFORM'
    | 'NEGOTIABLE'
    | 'NON_NEGOTIABLE'
    | 'PRE_AUTH_NEGOTIATION';
export type ShipmentType = 'EXTERNALLY_ARRANGED' | 'LOCAL_PICKUP' | 'FIRSTDIBS_FACILITATED';
export type BillingAddress = {
    firstName?: string | null;
    lastName?: string | null;
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    country?: string | null;
};
type TransactionState = {
    itemId?: string | null;
    buyerId?: string | null;
    transactionType?: TransactionType | null;
    quantity?: number | null;
    offerCurrency?: Currency | null;
    offerAmountPerItem?: number | null;
    currencyConversionRateEventId?: string | null;
    shipmentType?: ShipmentType | null;
    shipmentQuoteId?: string | null;
    shippingAddressId?: string | null;
    buyerChargeCurrency?: Currency | null;
    isTaxExempt?: boolean | null;
    promoCode?: string | null;
    billingAddress?: BillingAddress | null;
};
type ReduxState = {
    payment?: {
        klarnaSessionId?: string | null;
        klarnaClientToken?: string | null;
        klarnaNeedsSessionUpdate?: boolean | null;
    };
    transaction?: TransactionState;
    shipping?: {
        zipCode?: string | null;
        country?: string | null;
    };
} | null;
type Invoice = {
    readonly invoiceItems:
        | readonly ({
              readonly code: string | null;
              readonly totalAmount: {
                  readonly amount: number | null;
              } | null;
          } | null)[]
        | null;
} | null;

function useKlarnaSessionInput(): TransactionState {
    const transaction = useSelector<ReduxState, TransactionState>(
        state => state?.transaction || {}
    );
    const {
        itemId,
        buyerId,
        transactionType,
        quantity,
        offerAmountPerItem,
        offerCurrency,
        buyerChargeCurrency,
        shipmentType,
        shipmentQuoteId,
        shippingAddressId,
        currencyConversionRateEventId,
        isTaxExempt,
        promoCode,
    } = transaction;

    return {
        itemId,
        buyerId,
        transactionType,
        quantity,
        offerAmountPerItem,
        offerCurrency,
        buyerChargeCurrency,
        shipmentType,
        shipmentQuoteId,
        shippingAddressId,
        currencyConversionRateEventId,
        isTaxExempt,
        promoCode,
    };
}

export function useKlarnaSessionUpdateMutation(
    transactionId: string | null,
    klarnaSessionId: string | null
): [(billingAddress: BillingAddress | null) => Promise<void>, boolean] {
    const transaction = useKlarnaSessionInput();

    const [updateKlarnaSession, isInFlight] = useMutation<klarnaUpdateSessionMutation>(
        graphql`
            mutation klarnaUpdateSessionMutation($input: UpdateKlarnaSessionInput!) {
                updateKlarnaSession(input: $input) {
                    klarnaSessionId
                    klarnaClientToken
                }
            }
        `
    );

    function commitUpdate(billingAddress: BillingAddress | null): Promise<void> {
        const { transactionType, itemId } = transaction;
        return new Promise((resolve, reject) => {
            if (!klarnaSessionId || !transactionType || !itemId) {
                return;
            }

            updateKlarnaSession({
                variables: {
                    input: {
                        ...transaction,
                        transactionId,
                        klarnaSessionId,
                        transactionType,
                        itemId,
                        billingAddress,
                    },
                },
                onCompleted() {
                    resolve();
                },
                onError(err) {
                    reject(err);
                },
            });
        });
    }

    return [commitUpdate, isInFlight];
}

type KlarnaSessionConfig = {
    transactionId: string | null;
    billingAddress: BillingAddress;
    buyerInvoice?: Invoice;
};
type KlarnaSessionResult = {
    klarnaSessionId: string | null;
    klarnaClientToken: string | null;
    hasKlarnaSessionError?: boolean;
};
export function useCheckoutKlarnaSession(config: KlarnaSessionConfig): KlarnaSessionResult {
    const { transactionId, billingAddress, buyerInvoice } = config;

    const dispatch = useDispatch();
    const klarnaSessionId = useSelector<ReduxState, string | null>(
        state => state?.payment?.klarnaSessionId || null
    );
    const klarnaClientToken = useSelector<ReduxState, string | null>(
        state => state?.payment?.klarnaClientToken || null
    );
    const transaction = useKlarnaSessionInput();

    const [hasKlarnaSessionError, setHasKlarnaSessionError] = useState(false);

    const [createKlarnaSession, isInFlight] = useMutation<klarnaCreateSessionMutation>(
        graphql`
            mutation klarnaCreateSessionMutation($input: CreateKlarnaSessionInput!) {
                createKlarnaSession(input: $input) {
                    klarnaSessionId
                    klarnaClientToken
                }
            }
        `
    );

    useEffect(() => {
        const { transactionType, itemId } = transaction;
        if (
            !klarnaSessionId &&
            transactionType &&
            itemId &&
            !isInFlight &&
            !hasKlarnaSessionError
        ) {
            createKlarnaSession({
                variables: {
                    input: {
                        ...transaction,
                        transactionId,
                        transactionType,
                        itemId,
                        billingAddress,
                    },
                },
                onCompleted(response) {
                    const newKlarnaSessionId = response.createKlarnaSession?.klarnaSessionId;
                    const newKlarnaClientToken = response.createKlarnaSession?.klarnaClientToken;

                    if (newKlarnaSessionId && newKlarnaClientToken) {
                        dispatch(saveKlarnaSession(newKlarnaSessionId, buyerInvoice));
                        initKlarnaSdk(newKlarnaClientToken).then(() => {
                            dispatch(saveKlarnaClientToken(newKlarnaClientToken));
                        });
                    }
                },
                onError() {
                    setHasKlarnaSessionError(true);
                },
            });
        }
    }, [
        klarnaSessionId,
        transactionId,
        transaction,
        billingAddress,
        isInFlight,
        hasKlarnaSessionError,
        buyerInvoice,
        createKlarnaSession,
        dispatch,
    ]);

    return {
        klarnaSessionId,
        klarnaClientToken,
        hasKlarnaSessionError,
    };
}

export function useKlarnaSessionSync(transactionId: string | null): boolean {
    const dispatch = useDispatch();

    const klarnaSessionId = useSelector<ReduxState, string | null>(
        state => state?.payment?.klarnaSessionId || null
    );
    const klarnaNeedsSessionUpdate = useSelector<ReduxState, boolean>(
        state => state?.payment?.klarnaNeedsSessionUpdate || false
    );
    const billingAddress = useSelector<ReduxState, BillingAddress | null>(
        state => state?.transaction?.billingAddress || null
    );
    const [updateKlarnaSession, isUpdatingSession] = useKlarnaSessionUpdateMutation(
        transactionId,
        klarnaSessionId
    );

    useEffect(() => {
        if (klarnaSessionId && klarnaNeedsSessionUpdate) {
            updateKlarnaSession(billingAddress);
            dispatch(klarnaSessionUpdated());
        }
    }, [klarnaSessionId, billingAddress, klarnaNeedsSessionUpdate, updateKlarnaSession, dispatch]);

    return isUpdatingSession;
}

type KlarnaWidgetResult = {
    hasLoadedKlarnaWidget: boolean;
    showKlarnaForm: boolean;
};
export function useKlarnaPaymentsWidget(
    clientToken: string | null,
    container: string
): KlarnaWidgetResult {
    const [hasLoadedKlarnaWidget, setHasLoadedKlarnaWidget] = useState(false);
    const [showKlarnaForm, setShowKlarnaForm] = useState(false);

    useEffect(() => {
        if (clientToken && !hasLoadedKlarnaWidget) {
            setHasLoadedKlarnaWidget(false);
            setShowKlarnaForm(false);

            loadKlarnaPaymentsWidget(container).then(showForm => {
                setHasLoadedKlarnaWidget(true);
                setShowKlarnaForm(showForm);
            });
        }
    }, [clientToken, container, hasLoadedKlarnaWidget]);

    return {
        hasLoadedKlarnaWidget,
        showKlarnaForm,
    };
}
